/**
 * Hiring-demand board — the seats a team already has.
 *
 * A team with no open roles is invisible on a board built from the HDB: Talent
 * Acquisition runs 7 FTE and shows nothing, because the last reforecast planned
 * no new positions there. When somebody resigns, the backfill has to be raised
 * against a seat the HDB has never heard of — so this is where that starts.
 *
 * PEOPLE ARE FILED BY JOB TITLE, into the same teams the roles are in
 * (`teamForTitle`, server side). They used to sit in one undifferentiated list
 * at the bottom of the unit, which answered "how many" and nothing else — and
 * the row said "employee" where a role says what kind of role it is, which is a
 * word that tells the reader something they already knew. A person's row now
 * reads across exactly like a role's: who, level, what they do, and their FTE.
 *
 * Collapsed by default. It is context, not the work: the timeline is what the
 * round is about, and a unit of forty people would otherwise bury it.
 */
import { useState } from 'react';
import { Loader2, UserMinus } from 'lucide-react';
import type { RosterMember } from '../../../types/taDemand';
import { rosterSplit, sortedMembers } from './demandModel';
import type { Track } from './demandModel';
import { Caret, fmtFte, zoneClass } from './demandUi';
import { ACTIONS, CELL, NameCells } from './demandGrid';
import { MoveTo, MovedTag, type UnitChoice } from './Restructure';
import type { Placement, PlacementKind } from '../../../types/taDemand';

export function TeamRoster({
  members,
  tracks,
  depth,
  label = 'Current team',
  busy,
  backfills,
  onRequestBackfill,
  restructuring = false,
  unitChoices = [],
  deptName = '',
  unitName = '',
  onRestructure,
}: {
  members: RosterMember[];
  tracks: Track[];
  depth: number;
  /** What this group of seats is called where it sits. */
  label?: string;
  busy: Record<string, boolean>;
  /** employeeId → the draft already raised for that seat. */
  backfills: Map<string, string>;
  onRequestBackfill: (member: RosterMember) => void;
  /** Restructure mode: reveal the control that moves somebody to another unit. */
  restructuring?: boolean;
  unitChoices?: UnitChoice[];
  deptName?: string;
  unitName?: string;
  onRestructure?: (
    body: { kind: PlacementKind; id?: string; section?: string },
    to: Placement,
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!members.length) return null;

  const requested = members.filter((m) => backfills.has(m.id)).length;
  const split = rosterSplit(members);
  // Juniors first, by IC level. Airtable's own order was effectively random.
  const ordered = sortedMembers(members);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="group grid min-h-[34px] cursor-pointer items-stretch border-b border-gray-100 hover:bg-gray-50"
        style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
      >
        <NameCells
          depth={depth}
          className="group-hover:bg-gray-50"
          who={
            <>
              <Caret open={open} />
              <span className="truncate text-[13px] text-gray-500">{label}</span>
            </>
          }
        >
          <div className={CELL} />
          {/*
            INTERNAL FTE ONLY. An external is here and working but is not
            headcount, and somebody on parental leave is employed but not at
            work — adding either to the figure a lead plans against is the
            mistake this split exists to prevent. Both are still counted, beside
            it, because a backfill decision needs to know they are there.
          */}
          <div className={`${CELL} tabular-nums text-gray-500`}>{fmtFte(split.fte)} FTE</div>
          <div className={`${CELL} tabular-nums text-gray-400`}>
            {split.people} {split.people === 1 ? 'person' : 'people'}
          </div>
          <div className={CELL}>
            {split.external > 0 && (
              <span
                className="shrink-0 rounded-sm bg-sky-50 px-1 text-[10px] font-semibold leading-4 text-sky-700"
                title={`${split.external} external (${fmtFte(split.externalFte)} FTE), not counted as headcount`}
              >
                +{split.external} ext
              </span>
            )}
            {split.onLeave > 0 && (
              <span
                className="shrink-0 rounded-sm bg-amber-50 px-1 text-[10px] font-semibold leading-4 text-amber-700"
                title={`${split.onLeave} on leave (${fmtFte(split.onLeaveFte)} FTE), not counted`}
              >
                +{split.onLeave} away
              </span>
            )}
          </div>
          <div className={ACTIONS}>
            {requested > 0 && (
              <span className="shrink-0 rounded-sm bg-violet-50 px-1.5 text-[11px] font-semibold leading-4 text-violet-700">
                {requested} backfill{requested === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </NameCells>
        {tracks.map((t, i) => (
          <div key={i} className={zoneClass(t)} />
        ))}
      </div>

      {open &&
        ordered.map((m) => {
          const already = backfills.has(m.id);
          return (
            <div
              key={m.id}
              className="group grid min-h-[34px] items-stretch border-b border-gray-100 hover:bg-gray-50"
              style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
            >
              <NameCells
                /*
                  THE SAME DEPTH AS THE ROLES BESIDE THEM, not one deeper. The
                  extra level was structurally honest — these sit inside a
                  collapsible group — and it put every colleague's job title 16
                  pixels right of every role's, so the two lists you are meant to
                  read against each other did not start in the same place. The
                  caret on the header above is what marks the grouping.
                */
                depth={depth}
                className="group-hover:bg-gray-50"
                who={
                  /*
                    THE JOB TITLE FIRST, not the name — a role's first column is
                    what the seat is for, and a person's row has to answer the
                    same question in the same place or the two do not line up.
                    The name is column 4, beside "new hire / backfill", which is
                    the same question of a role: who is this seat about.
                  */
                  /*
                    GREY, where a role is black (Theresa). Both lists share the
                    same columns on purpose, so the thing that has to separate
                    them at a glance is weight: a seat somebody is already in is
                    context, and the roles are the work.
                  */
                  <span
                    className="min-w-0 truncate text-[13px] text-gray-500"
                    title={m.position || m.name}
                  >
                    {m.position || <span className="text-gray-400">no job title</span>}
                  </span>
                }
              >
                <div className={`${CELL} tabular-nums text-gray-500`}>{m.level || '—'}</div>
                <div className={`${CELL} tabular-nums text-gray-500`}>{fmtFte(m.fte)} FTE</div>
                <div className={CELL}>
                  <span className="min-w-0 truncate text-[13px] font-medium text-gray-600" title={m.name}>
                    {m.name}
                  </span>
                </div>
                {/*
                  Marked only when there is something to mark. An "internal"
                  badge on everybody marks nothing, and the point of the column
                  is that the exceptions are visible at a glance.
                */}
                <div className={CELL}>
                  {m.contractEnd && (
                    <span
                      className="shrink-0 rounded-sm bg-violet-50 px-1 text-[10px] font-semibold leading-4 text-violet-700"
                      title={`Fixed-term contract, ends ${m.contractEnd}. A seat that runs out is a hiring decision somebody has to take before then.`}
                    >
                      until {m.contractEnd.slice(0, 7)}
                    </span>
                  )}
                  <MovedTag
                    movedWithTeam={m.movedWithTeam}
                    placedBy={m.placedBy}
                    onUndo={
                      restructuring && onRestructure && m.placedBy
                        ? () => onRestructure({ kind: 'person', id: m.id, section: deptName }, {})
                        : undefined
                    }
                  />
                  {m.external && (
                    <span
                      className="shrink-0 rounded-sm bg-sky-50 px-1 text-[10px] font-semibold leading-4 text-sky-700"
                      title={`Employed externally${m.standing ? ` — "${m.standing}"` : ''}. Not counted as headcount.`}
                    >
                      external
                    </span>
                  )}
                  {m.onLeave && (
                    <span
                      className="shrink-0 rounded-sm bg-amber-50 px-1 text-[10px] font-semibold leading-4 text-amber-700"
                      title={`Employed, currently away${m.standing ? ` — "${m.standing}"` : ''}. Not counted in the FTE.`}
                    >
                      inactive
                    </span>
                  )}
                </div>
                <div className={ACTIONS}>
                  {/*
                    A colleague can be moved to another TEAM or another UNIT
                    entirely — which is the half of a reorg no map can infer,
                    because "Senior People & Organisation Manager" does not say
                    whether she is in Business Partnering or Culture & Community.
                    Only a person knows, so this is where they say it.
                  */}
                  {restructuring && onRestructure ? (
                    <MoveTo
                      what={m.name}
                      units={unitChoices}
                      current={{ department: deptName, unit: unitName, team: m.team }}
                      busy={!!busy[`place-person-${m.id}`]}
                      onMove={(to) =>
                        onRestructure({ kind: 'person', id: m.id, section: deptName }, to)
                      }
                    />
                  ) : already ? (
                    <span className="shrink-0 rounded-sm bg-violet-50 px-1.5 text-[11px] font-semibold leading-4 text-violet-700">
                      backfill raised
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRequestBackfill(m)}
                      disabled={!!busy[m.id]}
                      title={`Raise a backfill request for ${m.name}'s seat`}
                      className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-sm border border-gray-300 px-2 text-[11px] font-semibold text-gray-700 opacity-0 hover:border-violet-400 hover:bg-violet-50 hover:text-violet-700 focus:opacity-100 disabled:opacity-60 group-hover:opacity-100"
                    >
                      {busy[m.id] ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <UserMinus className="h-3 w-3" />
                      )}
                      Backfill
                    </button>
                  )}
                </div>
              </NameCells>
              {tracks.map((t, i) => (
                <div key={i} className={zoneClass(t)} />
              ))}
            </div>
          );
        })}
    </>
  );
}
